import {Component, ElementRef, inject, NgZone, OnDestroy, OnInit, PLATFORM_ID, signal, ViewChild} from '@angular/core';
import {DecimalPipe, isPlatformBrowser, NgClass, NgOptimizedImage} from '@angular/common';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {of, Subject} from 'rxjs';
import {catchError, filter, map, takeUntil} from 'rxjs/operators';
import {MatDialog} from '@angular/material/dialog';
import {Meta, MetaDefinition, Title} from '@angular/platform-browser';

import {ProductService} from '../../services/product.service';
import {NotificationService} from '../../notification/notification.service';
import {NotificationType} from '../../enum/notification-type.enum';
import {ConfigurationStore} from '../../store/configuration.store';
import {TypeOrderEnum} from '../../enum/type-order.enum';
import {OrderContextStore} from '../../store/order-context.store';
import {
  BundleCartItem,
  CartItem,
  CartItemType,
  CartService,
  ProductCartItem,
  PromotionCartItem
} from '../../services/cart.service';
import {HideFloatingCartService} from '../../services/hide-floating-cart.service';
import {OrderComponent} from '../../order/order.component';
import {THEME_CONFIG} from '../../../environments/tokens/theme.token';
import {TopNavbarComponent} from '../../shared/top-navbar/top-navbar.component';
import {NavbarComponent} from '../../shared/navbar/navbar.component';
import {MatIcon} from '@angular/material/icon';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatButton} from '@angular/material/button';
import {TenantService} from '../../services/tenant.service';
import {AdvertisementService} from '../../services/advertisement.service';
import {AuthenticationService} from '../../services/authentication.service';
import {BundleService} from '../../services/bundle.service';
import {PromosService} from '../../services/promos.service';
import {AdvertisementFlowService} from '../../services/advertisement-flow.service';
import {CustomerService} from '../../services/customer.service';

@Component({
  selector: 'app-details-product',
  templateUrl: './details-product.component.html',
  styleUrl: './details-product.component.scss',
  standalone: true,
  imports: [TopNavbarComponent, NavbarComponent, MatIcon, MatCheckbox, NgClass,
    MatButton, DecimalPipe, NgOptimizedImage]
})
export class DetailsProductComponent implements OnInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private destroy$ = new Subject<void>();
  isBrowser = isPlatformBrowser(this.platformId);
  private tenantService = inject(TenantService);
  isMilano = false;

  private ngZone = inject(NgZone);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private productService = inject(ProductService);
  private notificationService = inject(NotificationService);
  private orderContextStore = inject(OrderContextStore);
  private cartService = inject(CartService);
  readonly isSubmitting = signal<boolean>(false);
  private dialog = inject(MatDialog);
  private configStore = inject(ConfigurationStore);
  private metaService = inject(Meta);
  private titleService = inject(Title);
  private customerService = inject(CustomerService);
  private advertisementService = inject(AdvertisementService);
  private authenticationService = inject(AuthenticationService);
  private bundleService = inject(BundleService);
  private promosService = inject(PromosService);
  private advertisementQueue: any[] = [];
  private fromMenuFlow = false;
  private readonly SEEN_ADS_STORAGE_KEY = 'seen_advertisements';

  private metaTags: HTMLMetaElement[] = [];
  readonly restaurantAddress = this.configStore.address;
  hideFloatingCartService = inject(HideFloatingCartService);
  protected readonly themeConfig = inject(THEME_CONFIG);
  private adFlowService = inject(AdvertisementFlowService);
  @ViewChild('scrollableContent') scrollableContent!: ElementRef<HTMLDivElement>;

  productName!: string;
  productId!: string;
  product!: any;
  readonly isLoading = signal<boolean>(true);
  Object = Object;

  selectedItemQuantities: { [additionId: string]: { [itemId: string]: number } } = {};
  selectedSize: string | null = null;
  selectedPrice: number = 0;
  selectedIngredients: string[] = [];
  excludedIngredients: string[] = [];
  selectedSupplements: { [supplementId: string]: number } = {};
  supplementsByCategory: { [category: string]: any[] } = {};
  productQuantity: number = 1;

  isCartSheetOpen: boolean = false;
  touchStartY = 0;
  touchCurrentY = 0;
  sheetTransform = 'translateY(0px)';
  SWIPE_CLOSE_THRESHOLD = 120;

  selectedOrderType = this.orderContextStore.orderType;
  cityName = this.orderContextStore.cityName;
  zipCode = this.orderContextStore.zipcode;
  cityId = this.orderContextStore.cityId;
  deliveryDelay = this.orderContextStore.deliveryDelay;
  minOrderAmounts = this.orderContextStore.minOrderAmounts;

  editingCartItemId: string | null = null;
  private returnToCommand = false;
  private editingData: {
    existingSelectedIngredients?: string[];
    existingExcludedIngredients?: string[];
    existingSelectedAdditions?: any;
    existingSelectedSupplements?: any;
    existingQuantity?: number;
  } | null = null;

  discountReduction: any = null;
  discountActivatedFor: string[] = [];
  discountOrderTypes: string[] = [];
  discountPromotionId: string | null = null;

  get discountedBasePrice(): number {
    const base = this.selectedPrice || this.product?.price || 0;
    return this.applyReduction(base, this.discountReduction);
  }

  get isDelivery(): boolean {
    return this.selectedOrderType() === TypeOrderEnum.DELIVERY;
  }

  get isTakeawayOrOnSite(): boolean {
    const orderType = this.selectedOrderType();
    return orderType === TypeOrderEnum.TAKEAWAY || orderType === TypeOrderEnum.ON_SITE;
  }

  get filteredAdditions() {
    if (!this.product?.additions) return [];

    let additions = this.product.additions;
    if (this.selectedSize) {
      additions = additions.filter((addition: any) => {
        if (!addition.sizes || addition.sizes.length === 0) return true;
        return addition.sizes.some((sizeObj: any) => sizeObj.name === this.selectedSize);
      });
    }
    return additions.sort((a: any, b: any) => a.order - b.order);
  }

  ngOnInit() {
    this.isMilano = this.tenantService.getActiveTenant() === 'milano';

    this.hideFloatingCartService.setHide(true);
    this.productName = this.route.snapshot.paramMap.get('name') || '';

    this.loadProductWithState();

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.loadProductWithState();
    });
  }

  readonly loyaltyUnitCost = this.configStore.loyaltyUnitCost;

  get isRedeemMode(): boolean {
    return this.route.snapshot.queryParamMap.get('mode') === 'redeem' && !!this.loyaltyUnitCost();
  }

  toPoints(price: number): number {
    const unit = this.loyaltyUnitCost();
    return unit ? Math.round(price * 100 * unit) : 0;
  }

  formatBasePrice(price: number): string {
    if (this.isRedeemMode) return `${this.toPoints(price)} pts`;
    return `${this.truncate2(price).toFixed(2)} ${this.product.currency}`;
  }

  get totalPoints(): number {
    if (!this.isRedeemMode) return 0;
    const base = this.selectedPrice || this.product?.price || 0;
    return this.toPoints(base) * this.productQuantity;
  }

  formatTotal(): string {
    const euroTotal = this.calculateTotal();

    if (this.isRedeemMode) {
      const pts = this.totalPoints;
      if (euroTotal > 0) {
        return `${euroTotal.toFixed(2)} ${this.product?.currency || '€'} (${pts} pts)`;
      }
      return `${pts} pts`;
    }

    return `${euroTotal.toFixed(2)} ${this.product?.currency || '€'}`;
  }

  private loadProductWithState() {
    let state: any = null;
    const navigation = this.router.currentNavigation();

    if (navigation?.extras?.state) {
      state = navigation.extras.state;
    }

    if (!state && this.isBrowser) {
      state = (window as any).history.state;
    }

    this.returnToCommand = state?.returnToCommand === true;
    this.fromMenuFlow = state?.fromMenu === true;

    const newProductId = state?.slug || this.route.snapshot.paramMap.get('name');
    const newEditingCartItemId = state?.editingCartItemId || null;

    if (newProductId === this.productId && newEditingCartItemId === this.editingCartItemId) {
      if (this.fromMenuFlow) {
        this.checkAndShowAdvertisement();
      }
      return;
    }

    this.productId = newProductId;
    this.editingCartItemId = newEditingCartItemId;

    if (this.editingCartItemId) {
      this.editingData = {
        existingSelectedIngredients: state.existingSelectedIngredients || [],
        existingExcludedIngredients: state.existingExcludedIngredients || [],
        existingSelectedAdditions: state.existingSelectedAdditions || {},
        existingSelectedSupplements: state.existingSelectedSupplements || {},
        existingQuantity: state.existingQuantity || 1,
      };
    }

    this.discountReduction = state?.discountReduction || null;
    this.discountActivatedFor = state?.discountActivatedFor || [];
    this.discountOrderTypes = state?.discountOrderTypes || [];
    this.discountPromotionId = state?.discountPromotionId || null;
    this.selectedSize = state?.selectedSize || null;
    this.selectedPrice = state?.selectedPrice || 0;

    if (this.productId) {
      this.getProductBySlug(this.productId);
    } else {
      console.warn('Aucun slug de produit trouvé dans le state ou l\'itinéraire.');
    }
  }

  getProductBySlug(slug: string) {
    this.isLoading.set(true);

    this.productService.getProductBySlug(slug).pipe(
      catchError((err) => {
        console.error("Erreur de récupération du produit par slug", err);
        this.notificationService.errorHandler(err);
        this.isLoading.set(false);
        return of(null);
      }),
      takeUntil(this.destroy$)
    ).subscribe((result) => {
      if (!result) return;
      this.initializeProductProperties(result.body?.data);

      this.setSeoTags();

      if (this.fromMenuFlow) {
        this.checkAndShowAdvertisement();
        this.fromMenuFlow = false;
      }
    });
  }

  private getSeenAdvertisementIds(): string[] {
    if (!this.isBrowser) return [];
    try {
      const raw = localStorage.getItem(this.SEEN_ADS_STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  private markAdvertisementAsSeen(adId: string): void {
    if (!this.isBrowser) return;
    const seenIds = this.getSeenAdvertisementIds();
    if (!seenIds.includes(adId)) {
      seenIds.push(adId);
      localStorage.setItem(this.SEEN_ADS_STORAGE_KEY, JSON.stringify(seenIds));
    }
  }

  private checkAndShowAdvertisement(): void {
    if (!this.isBrowser) return;
    this.adFlowService.checkAndShowAdvertisements(this.cityId(), this.isDelivery);
  }


  openDialog(categoryId?: string) {
    const dialogRef = this.dialog.open(OrderComponent, {
      panelClass: 'custom-dialog',
      autoFocus: false,
      disableClose: true,
      data: {
        selectedCategoryId: categoryId,
        stayOnProductDetailsPage: true
      }
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.fromMenuFlow = true;
      this.checkAndShowAdvertisement();
    });
  }

  private findSizePriceForProduct(name: string): any | null {
    if (!this.product) return null;
    return this.product.pricePerSizes.find((pp: any) => pp.size.name === name);
  }

  private initializeProductProperties(data: any) {
    this.product = data;
    if (!this.product) {
      this.isLoading.set(false);
      return;
    }

    if (!this.selectedSize && this.product.pricePerSizes?.length > 0) {
      const selectedSize = this.route.snapshot.queryParamMap.get('size');
      if (selectedSize) {
        const pricePerSize = this.findSizePriceForProduct(selectedSize);
        if (pricePerSize) {
          this.selectedSize = selectedSize;
          this.selectedPrice = pricePerSize.price;
        } else {
          this.selectedSize = this.product.pricePerSizes[0].size.name;
          this.selectedPrice = this.product.pricePerSizes[0].price;
        }
      } else {
        this.selectedSize = this.product.pricePerSizes[0].size.name;
        this.selectedPrice = this.product.pricePerSizes[0].price;
      }
    }
    if (!this.selectedPrice && this.selectedSize) {
      const pricePerSize = this.findSizePriceForProduct(this.selectedSize);
      if (pricePerSize) this.selectedPrice = pricePerSize.price;
    }

    if (!this.selectedPrice && this.product.price && typeof this.product.price === 'number') {
      this.selectedPrice = this.product.price;
    }
    this.productName = this.product.name + (this.selectedSize === null ? '' : ` (${this.selectedSize})`);

    if (this.product.ingredients?.length) {
      this.selectedIngredients = this.product.ingredients.map((i: any) => i.id);
      this.excludedIngredients = [];
    }

    this.groupSupplementsByCategory();

    this.product.additions?.forEach((addition: any) => {
      this.selectedItemQuantities[addition.id] = {};
      addition?.items?.forEach((item: any) => {
        this.selectedItemQuantities[addition.id][item.id] = 0;
      });
    });

    if (this.editingCartItemId && this.editingData) {
      const data = this.editingData;
      if (data.existingSelectedIngredients && data.existingSelectedIngredients.length > 0) {
        this.selectedIngredients = [...data.existingSelectedIngredients];
        this.excludedIngredients = [...(data.existingExcludedIngredients ?? [])];
      }
      if (data.existingSelectedAdditions) {
        Object.keys(data.existingSelectedAdditions).forEach((addId: string) => {
          if (!this.selectedItemQuantities[addId]) {
            this.selectedItemQuantities[addId] = {};
          }
          const addData = data.existingSelectedAdditions[addId];
          Object.keys(addData).forEach((key: string) => {
            if (key !== 'size') {
              this.selectedItemQuantities[addId][key] = addData[key];
            }
          });
        });
      }
      if (data.existingSelectedSupplements) {
        this.selectedSupplements = {...data.existingSelectedSupplements};
      }
      if (data.existingQuantity) {
        this.productQuantity = data.existingQuantity;
      }
    }

    this.isLoading.set(false);
  }

  applyReduction(price: number, reduction: any): number {
    if (!reduction || !price) return price;
    if (reduction.type === 'PERCENTAGE') {
      return Number((price * (1 - reduction.value)).toFixed(2));
    }
    if (reduction.type === 'FIXED_AMOUNT') {
      return Number(Math.max(0, price - reduction.value).toFixed(2));
    }
    if (reduction.type === 'SET_PRICE') {
      return Number(reduction.value.toFixed(2));
    }
    return price;
  }

  formatReduction(reduction: any): string {
    if (!reduction) return '';
    if (reduction.type === 'PERCENTAGE') {
      return `-${Math.round(reduction.value * 100)}%`;
    }
    if (reduction.type === 'FIXED_AMOUNT') {
      return `-${reduction.value}€`;
    }
    return '';
  }

  getOrderTypeLabel(): string {
    const orderType = this.selectedOrderType();
    if (!orderType) return '';

    const labels: Record<TypeOrderEnum, string> = {
      [TypeOrderEnum.DELIVERY]: 'En Livraison',
      [TypeOrderEnum.TAKEAWAY]: 'À Emporter',
      [TypeOrderEnum.ON_SITE]: 'Sur Place'
    };
    return labels[orderType] || '';
  }

  getMinOrderAmount(): string {
    const amounts = this.minOrderAmounts();
    if (!amounts || amounts.length === 0) return '';

    const now = new Date();
    const currentTime = now.toTimeString().split(' ')[0];
    const timeToSeconds = (time: string): number => {
      const [hours, minutes, seconds] = time.split(':').map(Number);
      return hours * 3600 + minutes * 60 + seconds;
    };

    const currentSeconds = timeToSeconds(currentTime);

    for (const slot of amounts) {
      const fromSeconds = timeToSeconds(slot.from);
      const toSeconds = timeToSeconds(slot.to);

      if (fromSeconds <= toSeconds) {
        if (currentSeconds >= fromSeconds && currentSeconds <= toSeconds) {
          return slot.amount === 0 ? '' : `Min Cmd: ${slot.amount.toFixed(2)}€`;
        }
      } else {
        if (currentSeconds >= fromSeconds || currentSeconds <= toSeconds) {
          return slot.amount === 0 ? '' : `Min Cmd: ${slot.amount.toFixed(2)}€`;
        }
      }
    }

    const firstSlot = amounts[0];
    return firstSlot.amount === 0 ? '' : `Min Cmd: ${firstSlot.amount.toFixed(2)}€`;
  }

  getFormattedLocation() {
    if (this.isTakeawayOrOnSite) {
      return {
        text: this.restaurantAddress() || '',
        hasAddress: !!this.restaurantAddress()
      };
    }

    const city = this.cityName() || '';
    const zip = this.zipCode();
    const text = zip ? `${city} ${zip}` : city;

    return {text, hasAddress: false};
  }

  formatDeliveryDelay(): string {
    const delay = this.deliveryDelay();
    if (!delay) return '';

    const match = delay.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
    if (!match) return '';

    const hours = match[1] ? parseInt(match[1]) : 0;
    const minutes = match[2] ? parseInt(match[2]) : 0;

    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}min`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${minutes}min`;
    }
  }

  onTouchStart(event: TouchEvent) {
    this.touchStartY = event.touches[0].clientY;
  }

  onTouchMove(event: TouchEvent) {
    this.touchCurrentY = event.touches[0].clientY;
    const deltaY = this.touchCurrentY - this.touchStartY;
    if (deltaY > 0) {
      this.sheetTransform = `translateY(${deltaY}px)`;
    }
  }

  onTouchEnd() {
    const deltaY = this.touchCurrentY - this.touchStartY;
    if (deltaY > this.SWIPE_CLOSE_THRESHOLD) {
      this.closeCartSheet();
    } else {
      this.sheetTransform = 'translateY(0px)';
    }
  }

  closeCartSheet() {
    this.isCartSheetOpen = false;
    this.sheetTransform = 'translateY(0px)';
    if (this.isBrowser) {
      document.body.style.overflow = '';
    }
    if (this.scrollableContent) {
      this.scrollableContent.nativeElement.scrollTop = 0;
    }
  }

  toggleCartSheet() {
    this.isCartSheetOpen = !this.isCartSheetOpen;
    if (this.isBrowser) {
      document.body.style.overflow = this.isCartSheetOpen ? 'hidden' : '';
    }
    if (this.isCartSheetOpen && this.scrollableContent) {
      setTimeout(() => {
        if (this.scrollableContent) {
          this.scrollableContent.nativeElement.scrollTop = 0;
        }
      });
    }
  }

  groupSupplementsByCategory() {
    if (!this.product.supplements) return;
    this.supplementsByCategory = {};

    this.product.supplements.forEach((supplement: any) => {
      if (supplement.categories && supplement.categories.length > 0) {
        supplement.categories.forEach((category: string) => {
          if (!this.supplementsByCategory[category]) {
            this.supplementsByCategory[category] = [];
          }
          this.supplementsByCategory[category].push(supplement);
        });
      }
    });
  }

  getSupplementPrice(supplement: any): number {
    if (!this.selectedSize) return supplement.price || 0;

    const priceForSize = supplement.pricePerSizes?.find(
      (ps: any) => ps.size.name === this.selectedSize
    );
    return priceForSize?.price || supplement.price || 0;
  }

  truncate2(value: number): number {
    return Math.floor(value * 100) / 100;
  }

  toggleSupplement(supplementId: string) {
    if (this.selectedSupplements[supplementId]) {
      delete this.selectedSupplements[supplementId];
    } else {
      this.selectedSupplements[supplementId] = 1;
    }
  }

  isSupplementSelected(supplementId: string): boolean {
    return !!this.selectedSupplements[supplementId];
  }

  getSupplementCategories(): string[] {
    return Object.keys(this.supplementsByCategory);
  }

  getTotalQuantityForAddition(additionId: string): number {
    if (!this.selectedItemQuantities[additionId]) return 0;
    return Object.values(this.selectedItemQuantities[additionId])
      .reduce((sum: number, qty: number) => sum + qty, 0);
  }

  getValidationMessage(): string {
    if (!this.product?.additions) return '';

    const incompleteAdditions = this.filteredAdditions.filter((addition: any) => {
      if (addition.required) {
        const totalSelected = this.getTotalQuantityForAddition(addition.id);
        return totalSelected !== addition.quantity;
      }
      return false;
    });

    if (incompleteAdditions.length === 0) return '';

    const firstIncomplete = incompleteAdditions[0];
    const totalSelected = this.getTotalQuantityForAddition(firstIncomplete.id);
    const remaining = firstIncomplete.quantity - totalSelected;
    const categoryName = this.getAdditionTitle(firstIncomplete);

    return `Choisissez "${categoryName}" (${remaining} choix restant${remaining > 1 ? 's' : ''})`;
  }

  incrementItem(additionId: string, itemId: string, maxQuantity: number, index: number) {
    const totalQty = this.getTotalQuantityForAddition(additionId);

    if (totalQty >= maxQuantity) {
      const addition = this.product.additions.find((a: any) => a.id === additionId);
      const categoryName = this.getAdditionTitle(addition);
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        `Vous avez déjà choisi votre "${categoryName}"`,
        ''
      );
      return;
    }

    if (!this.selectedItemQuantities[additionId]) this.selectedItemQuantities[additionId] = {};
    if (!this.selectedItemQuantities[additionId][itemId]) this.selectedItemQuantities[additionId][itemId] = 0;

    this.selectedItemQuantities[additionId][itemId]++;
    const newTotal = this.getTotalQuantityForAddition(additionId);

    if (newTotal === maxQuantity) {
      this.scrollToNextAddition(index);
    }
  }

  scrollToNextAddition(index: number) {
    if (!this.isBrowser) return;

    const width = window.innerWidth;
    let targetId = '';
    let offset = 100;

    if (width < 768) {
      targetId = `mobile-component-${index + 1}`;
      offset = 80;
    } else if (width < 1024) {
      targetId = `tablet-component-${index + 1}`;
      offset = 100;
    } else {
      targetId = `desktop-component-${index + 1}`;
      offset = 120;
    }

    const next = document.getElementById(targetId);
    if (!next) return;

    const elementPosition = next.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }

  decrementItem(additionId: string, itemId: string) {
    if (!this.selectedItemQuantities[additionId]?.[itemId]) return;

    this.selectedItemQuantities[additionId][itemId]--;
    if (this.selectedItemQuantities[additionId][itemId] <= 0) {
      this.selectedItemQuantities[additionId][itemId] = 0;
    }
  }

  getItemQuantity(additionId: string, itemId: string): number {
    return this.selectedItemQuantities[additionId]?.[itemId] || 0;
  }

  onIngredientChange(event: any, ingredientId: string) {
    if (event.checked) {
      if (!this.selectedIngredients.includes(ingredientId)) {
        this.selectedIngredients.push(ingredientId);
      }
      this.excludedIngredients = this.excludedIngredients.filter(id => id !== ingredientId);
    } else {
      this.selectedIngredients = this.selectedIngredients.filter(id => id !== ingredientId);
      if (!this.excludedIngredients.includes(ingredientId)) {
        this.excludedIngredients.push(ingredientId);
      }
    }
  }

  getAdditionTitle(addition: any): string {
    if (addition.category?.name) return addition.category.name;
    if (addition.categories?.length > 0) return addition.categories[0];
    return 'Choix';
  }

  incrementProductQuantity() {
    this.productQuantity++;
  }

  decrementProductQuantity() {
    if (this.productQuantity > 1) {
      this.productQuantity--;
    }
  }

  hasSelectedAdditions(): boolean {
    return !!this.product?.additions?.some((addition: any) =>
      this.getTotalQuantityForAddition(addition.id) > 0
    );
  }

  calculateTotal(): number {
    if (!this.product) return 0;
    let base = this.selectedPrice || this.product.price || 0;
    base = this.applyReduction(base, this.discountReduction);

    let total = this.isRedeemMode ? 0 : base;
    this.product.additions?.forEach((addition: any) => {
      addition?.items?.forEach((item: any) => {
        const quantity = this.getItemQuantity(addition.id, item.id);
        if (quantity > 0 && item.price) {
          total += item.price * quantity;
        }
      });
    });

    Object.keys(this.selectedSupplements).forEach((supplementId) => {
      const supplement = this.product.supplements?.find((s: any) => s.id === supplementId);
      if (supplement) {
        const price = this.getSupplementPrice(supplement);
        total += price * this.selectedSupplements[supplementId];
      }
    });

    return Number((total * this.productQuantity).toFixed(2));
  }

  canAddToCart(): boolean {
    if (!this.filteredAdditions) return true;

    return this.filteredAdditions.every((addition: any) => {
      if (addition.required) {
        const totalSelected = this.getTotalQuantityForAddition(addition.id);
        return totalSelected === addition.quantity;
      }
      return true;
    });
  }

  addToCart() {
    if (this.isSubmitting()) return;

    if (!this.canAddToCart()) {
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        'Veuillez compléter tous les choix obligatoires',
        ''
      );
      return;
    }

    const existingProduct = this.cartService.productItems().find(
      item => item.product.id === this.product?.id && item.id !== this.editingCartItemId
    );

    if (existingProduct) {
      if (this.isRedeemMode && !existingProduct.redeemWithPoints) {
        this.notificationService.sendNotification(
          NotificationType.WARNING,
          `"${this.product.name}" est déjà dans votre panier en version payante. Retirez-le du panier pour l'ajouter avec vos points.`,
          ''
        );
        return;
      }

      if (!this.isRedeemMode && existingProduct.redeemWithPoints) {
        this.notificationService.sendNotification(
          NotificationType.WARNING,
          `"${this.product.name}" est déjà dans votre panier en version points de fidélité. Retirez-le du panier pour l'acheter en Euros.`,
          ''
        );
        return;
      }
    }

    if (this.isRedeemMode) {
      this.checkPointsThenAdd();
      return;
    }
    this.doAddToCart();
  }
  private fetchCustomerPoints() {
    return this.customerService.getProfile().pipe(
      map((res: any) => Number(res.body?.data?.loyaltyPoints ?? 0)),
      catchError(() => of(null))
    );
  }

  private getPointsAlreadyInCart(): number {
    return this.cartService.getPointsInCartExcluding(this.editingCartItemId);
  }
  private checkPointsThenAdd() {
    this.isSubmitting.set(true);
    this.fetchCustomerPoints().pipe(
      takeUntil(this.destroy$)
    ).subscribe(balance => {
      if (balance === null) {
        this.isSubmitting.set(false);
        this.notificationService.sendNotification(
          NotificationType.ERROR,
          'Connexion requise',
          'Veuillez vous connecter à votre compte pour utiliser vos points de fidélité.'
        );
        return;
      }

      const needed = this.totalPoints;
      const inCart = this.getPointsAlreadyInCart();
      const available = Math.max(balance - inCart, 0);

      if (needed > available) {
        this.isSubmitting.set(false);
        let detailMsg = `Ce produit nécessite ${needed} pts, mais vous n'avez que ${available} pts disponibles.`;
        if (inCart > 0) {
          detailMsg += ` (Solde total : ${balance} pts, dont ${inCart} pts déjà réservés dans votre panier).`;
        }
        this.notificationService.sendNotification(
          NotificationType.ERROR,
          'Solde de points insuffisant',
          detailMsg
        );
        return;
      }

      this.doAddToCart();
    });
  }
  getOrderTypeLabelFromKey(key: string): string {
    const typeKey = key as keyof typeof TypeOrderEnum;
    return TypeOrderEnum[typeKey] || key;
  }

  private getOrderTypeForAPI(): string {
    const orderType = this.orderContextStore.orderType();
    switch (orderType) {
      case TypeOrderEnum.DELIVERY:
        return 'DELIVERY';
      case TypeOrderEnum.TAKEAWAY:
        return 'TAKEAWAY';
      case TypeOrderEnum.ON_SITE:
        return 'ON_SITE';
      default:
        return 'TAKEAWAY';
    }
  }

  private findEntityNameInCart(entityType: string, entityId: string): string {
    if (!entityId) return '';

    // 1. Chercher dans le produit courant
    if (this.product) {
      if (entityType === 'product' && (this.product.id === entityId || this.product.slug === entityId)) {
        return this.product.name;
      }

      // Additions / Accompagnements
      if (this.product.additions && Array.isArray(this.product.additions)) {
        for (const addition of this.product.additions) {
          if (addition.items && Array.isArray(addition.items)) {
            const foundItem = addition.items.find((i: any) => i.id === entityId);
            if (foundItem) return foundItem.name;
          }
        }
      }

      // Suppléments
      if (this.product.supplements && Array.isArray(this.product.supplements)) {
        const foundSupp = this.product.supplements.find((s: any) => s.id === entityId);
        if (foundSupp) return foundSupp.name;
      }

      // Ingrédients
      if (this.product.ingredients && Array.isArray(this.product.ingredients)) {
        const foundIng = this.product.ingredients.find((ing: any) => ing.id === entityId);
        if (foundIng) return foundIng.name;
      }
    }

    // 2. Fallback dans le panier existant
    for (const item of this.cartService.items()) {
      if (item.type === CartItemType.PRODUCT && (item as ProductCartItem).product?.id === entityId) {
        return (item as ProductCartItem).product.name;
      }
      if (item.type === CartItemType.BUNDLE && (item as BundleCartItem).bundle?.id === entityId) {
        return (item as BundleCartItem).bundle.name;
      }
      if (item.type === CartItemType.PROMOTION && (item as PromotionCartItem).promotion?.id === entityId) {
        return (item as PromotionCartItem).promotion.title;
      }
    }

    return '';
  }

  private handleCartSaveError(err: any): void {
    const errorDetails = err.error?.details;
    const errorMsg = errorDetails?.message || err.error?.message || '';
    const errorValue = errorDetails?.value;

    if (
      (errorMsg.includes('type de commande') && errorMsg.includes('indisponible')) ||
      (err.error?.code === 'illegal_value' && ['DELIVERY', 'TAKEAWAY', 'ON_SITE'].includes(errorValue))
    ) {
      const rawType = errorValue || this.getOrderTypeForAPI();
      const translatedType = this.getOrderTypeLabelFromKey(rawType);
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        `Le mode "${translatedType}" n'est pas disponible actuellement.`,
        'Veuillez modifier votre type de commande.'
      );
      return;
    }

    let detailMsg: string = err.error?.details?.message || err.error?.message || '';
    Object.keys(TypeOrderEnum).forEach((key) => {
      if (detailMsg.includes(key)) {
        const translatedLabel = this.getOrderTypeLabelFromKey(key);
        detailMsg = detailMsg.replace(new RegExp(key, 'g'), translatedLabel);
      }
    });

    if (detailMsg.includes('CUSTOMER_NOT_ALLOWED')) {
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        `Ce code promo appartient à un autre utilisateur, vous n’avez pas le droit de l’utiliser.`,
        ''
      );
      return;
    }

    if (detailMsg.includes('NOT_STARTED_YET')) {
      const parts = detailMsg.split('/');
      const promoCode = parts[1] || '';
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        `Le code promo "${promoCode}" n'est pas encore disponible.`,
        ''
      );
      return;
    }

    if (detailMsg.includes('EXPIRED')) {
      const parts = detailMsg.split('/');
      const promoCode = parts[1] || '';
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        `Le code promo "${promoCode}" a expiré.`,
        ''
      );
      return;
    }

    if (detailMsg === 'ORDER_RATE_LIMIT_EXCEEDED') {
      const details = err.error?.details?.details;
      let timeMessage = 'quelques minutes';
      if (details?.allowedAfter) {
        const matchMinutes = details.allowedAfter.match(/PT(?:(\d+)M)?/);
        if (matchMinutes && matchMinutes[1]) {
          timeMessage = `${matchMinutes[1]} minute(s)`;
        }
      }
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        `Limite de commandes atteinte : Vous avez déjà passé ${details?.orders || 'plusieurs'} commande(s).`,
        `Par sécurité, merci de patienter encore ${timeMessage} avant de pouvoir commander à nouveau.`
      );
      return;
    }

    if (detailMsg === 'INSUFFICIENT_LOYALTY_POINTS') {
      const value = err.error?.details?.value;
      const available = value?.available ?? 0;
      const required = value?.required ?? 0;
      const missing = Math.max(0, required - available);
      const pts = (n: number) => `${n} point${n > 1 ? 's' : ''}`;

      this.notificationService.sendNotification(
        NotificationType.WARNING,
        `Points de fidélité insuffisants : vous avez ${pts(available)} et il en faut ${pts(required)}.`,
        missing > 0 ? `Il vous manque ${pts(missing)}.` : ''
      );
      return;
    }

    if (detailMsg.includes('USAGE_LIMIT_REACHED')) {
      const parts = detailMsg.split('/');
      const promoCode = parts[1] || '';
      const message = detailMsg.includes('PER_CUSTOMER')
        ? `Vous avez atteint la limite d'utilisation du code promo "${promoCode}".`
        : `Le code promo "${promoCode}" n'est plus disponible. La limite d'utilisation globale a été atteinte.`;

      this.notificationService.sendNotification(NotificationType.WARNING, message, '');
      return;
    }

    if (err.status === 404 || detailMsg.includes('/')) {
      if (detailMsg.startsWith('DeliveryCity/')) {
        this.notificationService.sendNotification(
          NotificationType.WARNING,
          `La ville de livraison n'est plus disponible. Merci de choisir une autre ville.`,
          ''
        );
        return;
      }

      const slashIndex = detailMsg.indexOf('/');
      const entityType = slashIndex !== -1 ? detailMsg.substring(0, slashIndex).toLowerCase() : '';
      const entityId = slashIndex !== -1 ? detailMsg.substring(slashIndex + 1).trim() : '';
      const foundName = this.findEntityNameInCart(entityType, entityId);

      const typeLabels: Record<string, string> = {
        promotion: 'La promotion',
        product: 'Le produit',
        bundle: 'Le menu',
        productingredient: "L'ingrédient",
        productsupplement: 'Le supplément'
      };
      const typeLabel = typeLabels[entityType] || "L'élément";

      if (foundName) {
        this.notificationService.sendNotification(
          NotificationType.WARNING,
          `${typeLabel} "${foundName}" n'est plus disponible. Merci de réviser votre sélection.`,
          ''
        );
      } else {
        this.notificationService.sendNotification(
          NotificationType.WARNING,
          `${typeLabel} n'est plus disponible. Merci de réviser votre sélection.`,
          ''
        );
      }
      return;
    }

    if (detailMsg) {
      this.notificationService.sendNotification(NotificationType.WARNING, detailMsg, '');
      return;
    }

    this.notificationService.errorHandler(err);
  }
  private doAddToCart() {
    this.isSubmitting.set(true); // 👈 Désactive le bouton dès le début
    const previousCartItemsSnapshot = JSON.parse(JSON.stringify(this.cartService.items()));

    let selectedSizeId: string | null = null;
    if (this.selectedSize) {
      const sizeObj = this.product.pricePerSizes?.find(
        (pps: any) => pps.size.name === this.selectedSize
      );
      selectedSizeId = sizeObj?.size?.id || this.product.pricePerSizes?.[0]?.size?.id || this.selectedSize;
    }

    const selectedAdditionsPayload: any = {};
    this.product.additions?.forEach((addition: any) => {
      const selectedItems = this.selectedItemQuantities[addition.id] || {};
      if (Object.keys(selectedItems).length > 0) {
        selectedAdditionsPayload[addition.id] = {
          ...selectedItems,
          size: addition.itemsSize?.id || null
        };
      }
    });

    const basePrice = this.selectedPrice || this.product.price || 0;
    const effectivePrice = this.applyReduction(basePrice, this.discountReduction);
    const pointsCost = this.isRedeemMode ? this.toPoints(basePrice) : null;

    if (this.editingCartItemId) {
      this.cartService.removeItem(this.editingCartItemId);
    }

    this.cartService.addProduct({
      product: this.product,
      productImage: this.product.image || '',
      selectedSize: selectedSizeId,
      selectedPrice: this.isRedeemMode ? 0 : effectivePrice,
      quantity: this.productQuantity,
      selectedIngredients: this.selectedIngredients,
      excludedIngredients: this.excludedIngredients,
      selectedAdditions: selectedAdditionsPayload,
      selectedSupplements: JSON.parse(JSON.stringify(this.selectedSupplements)),
      total: this.calculateTotal(),
      redeemWithPoints: this.isRedeemMode,
      pointsCost,
      discountActivatedFor: this.discountActivatedFor,
      discountOrderTypes: this.discountOrderTypes,
      discountPromotionId: this.discountPromotionId,
      reduction: this.discountReduction || null
    });

    if (this.isCartSheetOpen) {
      this.toggleCartSheet();
    }

    const apiOrderType = this.cartService.getOrderTypeForAPI();
    this.cartService.saveCartToServer(apiOrderType).subscribe({
      next: () => {
        // Le bouton RESTE désactivé pendant et après le succès
        this.editingCartItemId = null;
        if (this.returnToCommand) {
          this.router.navigate(
            ['/command'],
            { queryParams: this.isRedeemMode ? { mode: 'redeem' } : {} }
          );
        } else {
          this.notificationService.sendNotification(
            NotificationType.SUCCESS,
            'Produit mis à jour dans le panier !',
            ''
          );
          this.router.navigate(
            ['/product'],
            { queryParams: this.isRedeemMode ? { mode: 'redeem' } : {} }
          );
        }
      },
      error: (err) => {
        // 🔴 En cas d'erreur : on réactive le bouton et rollback
        this.isSubmitting.set(false);
        this.cartService.restoreItems(previousCartItemsSnapshot);
        this.handleCartSaveError(err);
      }
    });
  }
  private setSeoTags() {
    this.route.title.subscribe(title => {
      this.titleService.setTitle(`${title} - ${this.productName}`);
    });

    this.addTag({
      name: 'title',
      content: `${this.productName} - ${this.product?.category?.name} | ${this.configStore.name()}`
    });
    this.addTag({
      name: 'description',
      content: this.product.description || `${this.productName} à ${this.product.formattedPrice}. ${this.product.category.name} avec des ingrédients frais.`
    });
    this.addTag({
      name: 'keywords',
      content: `${this.productName}, ${this.product.category.name}, ${this.product.ingredients?.map((i: any) => i?.name).join(', ')}`
    });
    this.addTag({name: 'product:price:amount', content: this.selectedPrice.toString()});
    this.addTag({name: 'product:price:currency', content: this.product.currency});

    this.addTag({property: 'og:title', content: this.productName});
    this.addTag({property: 'og:description', content: this.product.description?.substring(0, 300)});
    this.addTag({property: 'og:image', content: this.product.image});
    this.addTag({property: 'og:image:alt', content: this.productName});
    this.addTag({property: 'og:type', content: 'product'});
    this.addTag({property: 'og:price:amount', content: this.selectedPrice.toString()});
    this.addTag({property: 'og:price:currency', content: this.product.currency});

    this.addTag({name: 'twitter:card', content: 'summary_large_image'});
    this.addTag({name: 'twitter:title', content: this.productName});
    this.addTag({name: 'twitter:description', content: this.product.description});
    this.addTag({name: 'twitter:image', content: this.product.image});
  }

  private addTag(tag: MetaDefinition, forceCreation?: boolean) {
    const t = this.metaService.addTag(tag, forceCreation);
    if (t) this.metaTags.push(t);
  }

  private removeSeoTags() {
    this.metaTags.forEach(tag => this.metaService.removeTagElement(tag));
    console.log(`Cleared ${this.metaTags.length} tags`);
    this.metaTags = [];
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.hideFloatingCartService.setHide(false);
    this.removeSeoTags();
    if (this.isBrowser) {
      document.body.style.overflow = '';
    }
    if (this.isBrowser) {
      if (!this.router.url.includes('/product')) {
        sessionStorage.removeItem('productPageState');
      }
    }
  }
}
